import React from 'react';
import '../styles/Landing.css';
import { PLANS, planPrice, registerUrl } from '../data/plans';
import {
  IconProjects,
  IconTasks,
  IconBoard,
  IconPortal,
  IconCollab,
  IconAutomation,
  IconActivity,
  IconBell,
  IconIntegrations,
  IconMobile,
  IconArrow,
  IconCheck,
  IconSpark,
  IconShield,
  IconChart,
} from '../components/Icons';

const webappUrl =
  process.env.REACT_APP_PABSYNC_WEBAPP_URL || 'https://app.pabsync.com';
const signupUrl = `${webappUrl}/register?plan=free&src=website`;

/* --- Section content ------------------------------------------------ */

const BENEFITS = [
  { icon: IconProjects, title: 'Project management', desc: 'Plan initiatives, set project keys, and keep every workstream in one place.' },
  { icon: IconTasks, title: 'Task tracking', desc: 'Statuses, assignees, labels, and time tracking with a full activity trail.' },
  { icon: IconBoard, title: 'Kanban boards', desc: 'Drag-and-drop boards with swim-lanes, WIP limits, and bulk edit.' },
  { icon: IconPortal, title: 'Customer portal', desc: 'Give clients a clean, scoped view of the work that matters to them.' },
  { icon: IconCollab, title: 'Team collaboration', desc: 'Shared boards, roles, and comments keep everyone moving together.' },
  { icon: IconAutomation, title: 'Workflow automation', desc: 'Trigger updates, assignments, and notifications without manual busywork.' },
  { icon: IconActivity, title: 'Activity logs', desc: 'Every change is recorded — full auditability across tasks and records.' },
  { icon: IconBell, title: 'Notifications', desc: 'Stay in the loop with a focused feed and Slack delivery.' },
  { icon: IconIntegrations, title: 'Integrations', desc: 'Connect the tools your team already runs on, with more on the way.' },
  { icon: IconMobile, title: 'Mobile friendly', desc: 'A responsive experience that works anywhere your team does.' },
];

const FEATURES = [
  {
    icon: IconBoard,
    title: 'Boards, backlog & calendar',
    desc: 'Switch between Kanban, backlog, list, and calendar views. Bulk-edit mode, WIP limits, and swim-lanes ship in the box.',
  },
  {
    icon: IconAutomation,
    title: 'Automations engine',
    desc: 'Event-driven rules with triggers, conditions, and actions — update fields, assign users, post to Slack, or call AI.',
  },
  {
    icon: IconSpark,
    title: 'Built-in AI assist',
    desc: 'Self-hosted AI summarizes, drafts, and enriches your work. Ask PabSync answers questions with cited sources.',
  },
  {
    icon: IconProjects,
    title: 'Custom fields & objects',
    desc: 'Model your process with per-project fields and user-defined objects — from CRM contacts to anything you need.',
  },
  {
    icon: IconChart,
    title: 'Dashboards & reports',
    desc: 'Account dashboards with configurable widgets and parameterized, whitelisted aggregations you can trust.',
  },
  {
    icon: IconShield,
    title: 'Roles & entitlements',
    desc: 'Granular IAM roles, permissions, and subscription-tier entitlements keep access exactly where it belongs.',
  },
];

const STEPS = [
  { n: '01', title: 'Create a workspace', desc: 'Sign up in under a minute and invite your team. No credit card required to start.' },
  { n: '02', title: 'Organize projects & tasks', desc: 'Set up projects, boards, custom fields, and backlogs that match how your team actually works.' },
  { n: '03', title: 'Collaborate and deliver', desc: 'Track progress, automate the busywork, and ship — with full visibility from day one.' },
];

const INTEGRATIONS = [
  { name: 'Slack', status: 'live' },
  { name: 'Google Workspace', status: 'live' },
  { name: 'Microsoft 365', status: 'soon' },
  { name: 'Microsoft Teams', status: 'soon' },
  { name: 'GitHub', status: 'soon' },
  { name: 'Trello', status: 'soon' },
  { name: 'Zoom', status: 'soon' },
  { name: 'Dropbox', status: 'soon' },
  { name: 'OneDrive', status: 'soon' },
];

const TESTIMONIALS = [
  {
    quote: 'PabSync gave us one place for projects, tasks, and client visibility. Onboarding took an afternoon, not a quarter.',
    name: 'Operations Lead',
    org: 'Software agency',
  },
  {
    quote: 'The automations and activity log mean nothing slips. We finally trust the board to be the single source of truth.',
    name: 'Engineering Manager',
    org: 'Product team',
  },
  {
    quote: 'Clean, fast, and no bloat. It feels like a tool built by people who ship, for people who ship.',
    name: 'Founder',
    org: 'Early-stage startup',
  },
];

const FAQS = [
  {
    q: 'How do I get started?',
    a: 'Create a free account in under a minute — no credit card required. Set up your first workspace, invite teammates, and start organizing projects right away.',
  },
  {
    q: 'How does collaboration work?',
    a: 'Invite your team, assign roles, and share boards, backlogs, and calendars. Comments, activity logs, and notifications keep everyone aligned in real time.',
  },
  {
    q: 'Which integrations are available?',
    a: 'Slack and Google Workspace (Drive, Calendar, and Gmail) are live today. Microsoft 365, Microsoft Teams, Zoom, Dropbox, OneDrive, Trello, and GitHub are on the roadmap and marked “Coming soon” in the Apps Center.',
  },
  {
    q: 'Is my data secure?',
    a: 'PabSync is multi-tenant with account-scoped access, granular IAM roles and permissions, secure httpOnly session cookies, and full activity auditing. Self-hosted AI means your content is not sent to third-party model providers.',
  },
  {
    q: 'How does pricing work?',
    a: 'Start free, upgrade to Starter for unlimited projects, or choose Team for clear per-user pricing. You can change plans at any time — billing is handled securely in the web app.',
  },
  {
    q: 'Can I use PabSync on mobile?',
    a: 'Yes. The experience is fully responsive and works across mobile, tablet, and desktop.',
  },
];

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

/* --- Product mockup (pure CSS/SVG, no image request) ---------------- */

const ProductMock: React.FC = () => (
  <div className="mock" aria-hidden="true">
    <div className="mock__bar">
      <span className="mock__dot" />
      <span className="mock__dot" />
      <span className="mock__dot" />
      <span className="mock__url">app.pabsync.com</span>
    </div>
    <div className="mock__body">
      <aside className="mock__side">
        <div className="mock__side-brand" />
        <div className="mock__side-item is-active" />
        <div className="mock__side-item" />
        <div className="mock__side-item" />
        <div className="mock__side-item" />
        <div className="mock__side-item mock__side-item--short" />
      </aside>
      <div className="mock__main">
        <div className="mock__toolbar">
          <div className="mock__title-line" />
          <div className="mock__chip" />
        </div>
        <div className="mock__cols">
          {[
            { label: 'Backlog', cards: 3 },
            { label: 'In progress', cards: 2, accent: true },
            { label: 'Review', cards: 2 },
            { label: 'Done', cards: 3 },
          ].map((col, i) => (
            <div className="mock__col" key={i}>
              <div className="mock__col-head">
                <span />
                <b>{col.cards}</b>
              </div>
              {Array.from({ length: col.cards }).map((_, c) => (
                <div
                  className={`mock__card${col.accent && c === 0 ? ' is-accent' : ''}`}
                  key={c}
                >
                  <div className="mock__card-line" />
                  <div className="mock__card-line mock__card-line--short" />
                  <div className="mock__card-foot">
                    <span className="mock__tag" />
                    <span className="mock__avatar" />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

/* --- Page ----------------------------------------------------------- */

const HomePage: React.FC = () => {
  return (
    <div className="lp">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <main id="main">
        {/* HERO */}
        <section className="hero">
          <div className="hero__grid-bg" aria-hidden="true" />
          <div className="container hero__inner">
            <div className="hero__copy">
              <span className="eyebrow">
                <span className="eyebrow__dot" /> Human + AI work management
              </span>
              <h1>
                The work platform that keeps engineering teams
                <span className="hero__accent"> in sync</span>.
              </h1>
              <p className="hero__lede">
                PabSync unifies projects, tasks, boards, and automation in one
                fast, clutter-free workspace — with built-in AI that helps your
                team plan, track, and deliver.
              </p>

              <div className="hero__ctas">
                <a className="btn btn--primary btn--lg" href={signupUrl}>
                  Start free <IconArrow size={18} />
                </a>
                <a className="btn btn--ghost btn--lg" href="/#features">
                  Learn more
                </a>
              </div>

              <ul className="hero__reassure">
                <li>
                  <IconCheck size={16} /> No credit card required
                </li>
                <li>
                  <IconCheck size={16} /> Free plan forever
                </li>
                <li>
                  <IconCheck size={16} /> Set up in minutes
                </li>
              </ul>
            </div>

            <div className="hero__media">
              <ProductMock />
            </div>
          </div>

          {/* Social proof strip */}
          <div className="container">
            <div className="logos">
              <span className="logos__label">
                Built for modern software and operations teams
              </span>
              <div className="logos__row">
                {['Northwind', 'Quanta', 'Meridian', 'Loopwork', 'Cedar', 'Vanta Labs'].map(
                  (l) => (
                    <span className="logos__item" key={l}>
                      {l}
                    </span>
                  )
                )}
              </div>
            </div>
          </div>
        </section>

        {/* WHY PABSYNC */}
        <section className="section" id="why">
          <div className="container">
            <div className="section__head">
              <span className="eyebrow eyebrow--muted">Why PabSync</span>
              <h2>Everything your team needs to plan and deliver</h2>
              <p className="section__sub">
                One platform for the full lifecycle of work — no sprawling stack,
                no per-seat AI tax, no clutter.
              </p>
            </div>

            <div className="bento">
              {BENEFITS.map((b) => (
                <div className="bento__item" key={b.title}>
                  <span className="bento__icon">
                    <b.icon size={20} />
                  </span>
                  <div>
                    <h3>{b.title}</h3>
                    <p>{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="section section--alt" id="features">
          <div className="container">
            <div className="section__head">
              <span className="eyebrow eyebrow--muted">Features</span>
              <h2>Powerful where it counts, simple everywhere else</h2>
              <p className="section__sub">
                The capabilities of an enterprise platform, delivered with the
                clarity of a tool your team will actually enjoy using.
              </p>
            </div>

            <div className="cards">
              {FEATURES.map((f) => (
                <article className="card" key={f.title}>
                  <span className="card__icon">
                    <f.icon size={22} />
                  </span>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="section" id="how">
          <div className="container">
            <div className="section__head">
              <span className="eyebrow eyebrow--muted">How it works</span>
              <h2>Up and running in three steps</h2>
            </div>

            <ol className="steps">
              {STEPS.map((s) => (
                <li className="step" key={s.n}>
                  <span className="step__n">{s.n}</span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* INTEGRATIONS */}
        <section className="section section--alt" id="integrations">
          <div className="container">
            <div className="section__head">
              <span className="eyebrow eyebrow--muted">Integrations</span>
              <h2>Connect the tools you already use</h2>
              <p className="section__sub">
                Slack and Google Workspace are live today, with more landing
                across the roadmap.
              </p>
            </div>

            <div className="integrations">
              {INTEGRATIONS.map((i) => (
                <div className="integration" key={i.name}>
                  <span className="integration__mark">{i.name.charAt(0)}</span>
                  <span className="integration__name">{i.name}</span>
                  <span
                    className={`integration__status integration__status--${i.status}`}
                  >
                    {i.status === 'live' ? 'Live' : 'Coming soon'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="section" id="testimonials">
          <div className="container">
            <div className="section__head">
              <span className="eyebrow eyebrow--muted">Social proof</span>
              <h2>Teams move faster on PabSync</h2>
            </div>

            <div className="quotes">
              {TESTIMONIALS.map((t, i) => (
                <figure className="quote" key={i}>
                  <blockquote>“{t.quote}”</blockquote>
                  <figcaption>
                    <span className="quote__avatar" aria-hidden="true" />
                    <span>
                      <b>{t.name}</b>
                      <small>{t.org}</small>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING PREVIEW */}
        <section className="section section--alt" id="pricing">
          <div className="container">
            <div className="section__head">
              <span className="eyebrow eyebrow--muted">Pricing</span>
              <h2>Simple, transparent pricing</h2>
              <p className="section__sub">
                Start free and upgrade when you’re ready. No hidden fees.
              </p>
            </div>

            <div className="plans">
              {PLANS.map((p) => {
                const price = planPrice(p);
                return (
                  <div
                    className={`plan${p.isPopular ? ' plan--featured' : ''}`}
                    key={p.id}
                  >
                    {p.badge && <span className="plan__badge">{p.badge}</span>}
                    <h3 className="plan__name">{p.name}</h3>
                    <p className="plan__blurb">{p.recommendedFor}</p>
                    <div className="plan__price">
                      <span className="plan__amount">{price.amount}</span>
                      <span className="plan__unit">{price.unit}</span>
                    </div>
                    <a
                      className={`btn ${p.isPopular ? 'btn--primary' : 'btn--ghost'} btn--block`}
                      href={registerUrl(p.id)}
                    >
                      {p.ctaLabel}
                    </a>
                    <ul className="plan__features">
                      {p.teaser.map((f) => (
                        <li key={f}>
                          <IconCheck size={16} /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>

            <p className="plans__foot">
              Need the full comparison?{' '}
              <a href="/pricing">See detailed pricing →</a>
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="section" id="faq">
          <div className="container container--narrow">
            <div className="section__head">
              <span className="eyebrow eyebrow--muted">FAQ</span>
              <h2>Frequently asked questions</h2>
            </div>

            <div className="faq">
              {FAQS.map((f, i) => (
                <details className="faq__item" key={i}>
                  <summary>
                    {f.q}
                    <span className="faq__chevron" aria-hidden="true" />
                  </summary>
                  <div className="faq__answer">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="section cta">
          <div className="container">
            <div className="cta__panel">
              <div className="cta__grid-bg" aria-hidden="true" />
              <h2>Ready to get your team in sync?</h2>
              <p>
                Create a free account in under a minute — or talk to us about
                rolling PabSync out across your organization.
              </p>
              <div className="cta__actions">
                <a className="btn btn--primary btn--lg" href={signupUrl}>
                  Start free <IconArrow size={18} />
                </a>
                <a className="btn btn--metal btn--lg" href="/contact">
                  Contact us
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;
